import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { LabelKey } from '../models/report-labels.model';
import { ReportLabelsService } from '../../features/manage-settings/report-labels/report-labels.service';

@Injectable({
  providedIn: 'root',
})
export class PageTitleService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly titleSubject = new BehaviorSubject<string>('Home');
  readonly title$ = this.titleSubject
    .asObservable()
    .pipe(distinctUntilChanged());
  fidelityCheckLabel: string = LabelKey.FIDELITY_CHECK;

  private readonly routeTitles = new Map([
    ['', 'Home'],
    ['/dashboard', 'Home'],
    ['/organizations', 'Organizations'],
    ['/activity-dashboard', 'Activity Dashboard'],
    ['/dashboard-activity', 'Dashboard'],
    ['/insights', 'Insights'],
    ['/user-management-listing/manage-organization', 'User Management Listing'],
    ['/strategies', 'Strategies'],
    ['/manage-settings', 'Manage Settings'],
    ['/manage-settings/custom-time', 'Custom Time'],
    ['/manage-settings/custom-question', 'Custom Question'],
    ['/manage-settings/questionnaire', 'Questionnaire'],
    ['/manage-settings/logo', 'Logo'],
    ['/manage-settings/options', 'Options'],
    ['/manage-settings/fidelity-check', 'Fidelity Check'],
    ['/manage-settings/reference-materials', 'Reference Materials'],
    ['/manage-settings/titles', 'Titles'],
    ['/manage-settings/behavior-goals', 'Behavior Goals'],
    ['/manage-settings/notes', 'Notes'],
    ['/manage-settings/report-labels', 'Report Labels'],
    ['/reports', 'Reports'],
    ['/profile', 'Profile'],
    ['/support', 'Frequently Asked Questions'],
    ['/notifications', 'Notifications'],
    ['/user-management', 'Users'],
    ['/bip-template', 'BIP Template'],
    ['/insight-admin', 'Insights Admin'],
    ['/user-search', 'User Directory'],
    ['/broadcast-config', 'Broadcast Config'],
    ['/idp', 'Identity Provider'],
    ['/audit-logs', 'Activity Logs'],
    //here you can add more routes and their corresponding titles
  ]);

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly reportLabelsService: ReportLabelsService) {
    this.reportLabelsService
      .getLabelValue(LabelKey.FIDELITY_CHECK)
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.fidelityCheckLabel = value;
        const currentUrl = this.router.url.split('?')[0];
        if (currentUrl === '/fidelity-check') {
          this.titleSubject.next(value);
        } else if (currentUrl === '/manage-settings/fidelity-check') {
          this.titleSubject.next(`${value}`);
        }
      });
    this.initializeRouteListener();
  }

  private initializeRouteListener(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(event => {
        const baseUrl = event.url.split('?')[0];

        if (baseUrl === '/fidelity-check') {
          this.titleSubject.next(this.fidelityCheckLabel);
        } else if (baseUrl === '/manage-settings/fidelity-check') {
          this.titleSubject.next(`${this.fidelityCheckLabel}`);
        } else {
          const title = this.routeTitles.get(baseUrl) ?? '';
          this.titleSubject.next(title);
        }
      });
  }

  setTitle(title: string): void {
    this.titleSubject.next(title);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
